import { useEffect, useState, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Package, PoundSterling, MapPin, Phone, Mail, Calendar, Send, Truck, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';

// Provider Dashboard, Orders tab. Phase 3 of the retail upsell stack.
//
// Surfaces every PAID retail line across the provider's bookings as a
// flat packing-slip list so the provider can dispatch product orders
// without trawling through the booking detail view. The lifecycle of
// each line is intentionally decoupled from the underlying booking,
// reschedules and cancellations leave fulfilment state untouched.

const STATE_LABELS = {
  unfulfilled: 'Unfulfilled',
  packed: 'Packed',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

// Allowed next-state options per current state. Keeps the UI honest
// so providers don't accidentally bounce backwards through the ladder.
// `cancelled` is intentionally terminal in the UI: once it fires, a
// Stripe refund has been issued and the line's payment_status flips
// to "refunded", which the backend refuses to mutate further.
const NEXT_STATES = {
  unfulfilled: ['packed', 'cancelled'],
  packed: ['dispatched', 'unfulfilled', 'cancelled'],
  dispatched: ['delivered', 'packed'],
  delivered: [],
  cancelled: [],
};

const STATE_BADGE = {
  unfulfilled: 'bg-amber-100 text-amber-900 border-amber-200',
  packed: 'bg-blue-100 text-blue-900 border-blue-200',
  dispatched: 'bg-indigo-100 text-indigo-900 border-indigo-200',
  delivered: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  cancelled: 'bg-stone-200 text-stone-700 border-stone-300',
};

function formatGBP(n) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
  }).format(Number(n) || 0);
}

function formatAddress(addr) {
  if (!addr) return null;
  if (typeof addr === 'string') return addr;
  const parts = [addr.line1, addr.line2, addr.city, addr.postcode]
    .filter(Boolean);
  return parts.join(', ');
}

function formatRelativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB');
}

// Mirror of utils/tracking.CARRIER_DISPLAY on the backend. Keep this
// list in sync if a new carrier is added.
const CARRIER_LABEL = {
  royal_mail: 'Royal Mail',
  parcelforce: 'Parcelforce',
  dpd: 'DPD',
  evri: 'Evri',
  ups: 'UPS',
  dhl: 'DHL',
  fedex: 'FedEx',
  unknown: 'Courier',
};

export function OrdersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState({}); // {`${booking_id}:${product_id}`: true}
  const [trackingDrafts, setTrackingDrafts] = useState({}); // {`${booking_id}:${product_id}`: 'AB123...'}

  const fetchOrders = useCallback(async () => {
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await api.get('/retail/orders', { params });
      setRows(res.data || []);
    } catch (e) {
      toast.error('Could not load retail orders');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (row, nextStatus) => {
    const key = `${row.booking_id}:${row.product_id}`;
    // Phase 4: cancelling a paid line triggers a Stripe refund. Surface
    // that to the provider before they click through so they don't
    // accidentally refund a client they meant to dispatch to.
    if (nextStatus === 'cancelled') {
      const confirmed = window.confirm(
        `Cancelling this line will refund £${(row.line_total || 0).toFixed(2)} to ${row.client_name || 'the client'} via Stripe. Continue?`,
      );
      if (!confirmed) return;
    }
    try {
      setUpdating((u) => ({ ...u, [key]: true }));
      const res = await api.put(
        `/retail/orders/${row.booking_id}/${row.product_id}`,
        { fulfillment_status: nextStatus },
      );
      const refund = res.data?.retail_refund;
      if (nextStatus === 'cancelled' && refund?.retail_refund_amount > 0) {
        toast.success(`Refunded £${refund.retail_refund_amount.toFixed(2)} to client`);
      } else if (nextStatus === 'cancelled' && refund?.stripe_error) {
        toast.error(`Marked cancelled, but refund failed: ${refund.stripe_error}`);
      } else {
        toast.success(`Marked as ${STATE_LABELS[nextStatus] || nextStatus}`);
      }
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update order');
    } finally {
      setUpdating((u) => {
        const { [key]: _drop, ...rest } = u;
        return rest;
      });
    }
  };

  const sendPackingSlip = async (row) => {
    const key = `${row.booking_id}:${row.product_id}:slip`;
    try {
      setUpdating((u) => ({ ...u, [key]: true }));
      const res = await api.post(
        `/retail/orders/${row.booking_id}/${row.product_id}/email-packing-slip`,
      );
      const to = res.data?.client_email;
      toast.success(to ? `Packing slip sent to ${to}` : 'Packing slip sent');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send packing slip');
    } finally {
      setUpdating((u) => {
        const { [key]: _drop, ...rest } = u;
        return rest;
      });
    }
  };

  const saveTracking = async (row, clear = false) => {
    const key = `${row.booking_id}:${row.product_id}:track`;
    const draftKey = `${row.booking_id}:${row.product_id}`;
    const number = clear ? '' : (trackingDrafts[draftKey] || '').trim();
    if (!clear && !number) {
      toast.error('Enter a tracking number to save');
      return;
    }
    try {
      setUpdating((u) => ({ ...u, [key]: true }));
      const res = await api.put(
        `/retail/orders/${row.booking_id}/${row.product_id}/tracking`,
        { tracking_number: number },
      );
      if (clear) {
        toast.success('Tracking removed');
      } else {
        const carrier = CARRIER_LABEL[res.data?.tracking_carrier] || 'Courier';
        const smsStatus = res.data?.sms_status;
        const okStatuses = ['queued', 'sent', 'delivered', 'accepted', 'sending'];
        if (okStatuses.includes(smsStatus)) {
          toast.success(`Tracking saved (${carrier}). Client notified by SMS.`);
        } else if (smsStatus === 'no_phone_on_file') {
          toast.success(`Tracking saved (${carrier}). No phone on file, SMS skipped.`);
        } else if (smsStatus === 'failed') {
          toast.warning(`Tracking saved (${carrier}). SMS failed to send.`);
        } else if (smsStatus === 'unsubscribed') {
          toast.success(`Tracking saved (${carrier}). Client has opted out of SMS.`);
        } else {
          toast.success(`Tracking saved (${carrier})`);
        }
      }
      setTrackingDrafts((d) => {
        const { [draftKey]: _drop, ...rest } = d;
        return rest;
      });
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save tracking');
    } finally {
      setUpdating((u) => {
        const { [key]: _drop, ...rest } = u;
        return rest;
      });
    }
  };

  const counts = rows.reduce((acc, r) => {
    acc[r.fulfillment_status] = (acc[r.fulfillment_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="orders-tab-content">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl text-[#1A1A1A]">Retail Orders</h2>
          <p className="text-stone-500 text-sm mt-1">
            Paid products bought alongside a service. Mark each line as you pack, dispatch and deliver.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="orders-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E6E4DD] rounded-lg p-12 text-center text-stone-500">
          Loading orders…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#E6E4DD] rounded-lg p-12 text-center"
          data-testid="orders-empty-state"
        >
          <Package className="h-10 w-10 mx-auto text-stone-300 mb-3" />
          <p className="font-heading text-lg text-[#1A1A1A]">No retail orders yet</p>
          <p className="text-stone-500 text-sm mt-1 max-w-md mx-auto">
            When a client picks one of your recommended products at checkout, the order will land here for you to dispatch.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(STATE_LABELS).map(([key, label]) => (
              <span
                key={key}
                className={`px-2.5 py-1 rounded-full border ${STATE_BADGE[key]}`}
              >
                {label}: {counts[key] || 0}
              </span>
            ))}
          </div>

          <div className="space-y-3" data-testid="orders-list">
            {rows.map((row) => {
              const key = `${row.booking_id}:${row.product_id}`;
              const allowedNext = row.payment_status === 'refunded'
                ? []
                : (NEXT_STATES[row.fulfillment_status] || []);
              const shipTo = formatAddress(row.ship_to);
              return (
                <div
                  key={key}
                  className="bg-white border border-[#E6E4DD] rounded-lg p-4 sm:p-5"
                  data-testid={`order-row-${row.product_id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading text-lg text-[#1A1A1A] truncate">
                          {row.product_name}
                        </h3>
                        <Badge
                          variant="outline"
                          className={`text-xs ${STATE_BADGE[row.fulfillment_status]}`}
                          data-testid={`order-status-${row.product_id}`}
                        >
                          {STATE_LABELS[row.fulfillment_status] || row.fulfillment_status}
                        </Badge>
                        {row.payment_status === 'refunded' && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-rose-50 text-rose-900 border-rose-200"
                            data-testid={`order-refunded-${row.product_id}`}
                          >
                            Refunded
                          </Badge>
                        )}
                      </div>
                      <p className="text-stone-500 text-sm mt-1">
                        {row.qty} × {formatGBP(row.unit_price)} = <strong className="text-[#1A1A1A]">{formatGBP(row.line_total)}</strong>
                      </p>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-stone-600">
                        <div className="flex items-start gap-2">
                          <Calendar className="h-4 w-4 mt-0.5 text-stone-400 shrink-0" />
                          <span>
                            <span className="text-stone-400">Booking </span>
                            <span className="font-medium text-[#1A1A1A]">{row.booking_ref || row.booking_id}</span>
                            <span className="text-stone-400"> · {row.service_name}</span>
                            <br />
                            <span className="text-stone-400">{row.appointment_date} at {row.appointment_time}</span>
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <PoundSterling className="h-4 w-4 mt-0.5 text-stone-400 shrink-0" />
                          <span>
                            <span className="text-stone-400">Client </span>
                            <span className="font-medium text-[#1A1A1A]">{row.client_name}</span>
                          </span>
                        </div>
                        {row.client_email && (
                          <div className="flex items-start gap-2">
                            <Mail className="h-4 w-4 mt-0.5 text-stone-400 shrink-0" />
                            <a
                              href={`mailto:${row.client_email}`}
                              className="text-[#1A1A1A] hover:underline truncate"
                            >
                              {row.client_email}
                            </a>
                          </div>
                        )}
                        {row.client_phone && (
                          <div className="flex items-start gap-2">
                            <Phone className="h-4 w-4 mt-0.5 text-stone-400 shrink-0" />
                            <a
                              href={`tel:${row.client_phone}`}
                              className="text-[#1A1A1A] hover:underline"
                            >
                              {row.client_phone}
                            </a>
                          </div>
                        )}
                        {shipTo && (
                          <div className="flex items-start gap-2 sm:col-span-2">
                            <MapPin className="h-4 w-4 mt-0.5 text-stone-400 shrink-0" />
                            <span className="text-stone-600">
                              <span className="text-stone-400">Ship to: </span>{shipTo}
                            </span>
                          </div>
                        )}

                        {/* Tracking row, only for paid lines past the "packed" stage */}
                        {row.payment_status === 'paid' && ['packed', 'dispatched', 'delivered'].includes(row.fulfillment_status) && (
                          <div className="flex items-start gap-2 sm:col-span-2 mt-1">
                            <Truck className="h-4 w-4 mt-2 text-stone-400 shrink-0" />
                            {row.tracking_number ? (
                              <div className="flex flex-col gap-1" data-testid={`order-tracking-display-${row.product_id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-stone-400 mt-1.5">Tracking:</span>
                                  <Badge variant="outline" className="bg-stone-50 text-[#1A1A1A] border-stone-300 font-mono text-[12px]">
                                    {CARRIER_LABEL[row.tracking_carrier] || 'Courier'} · {row.tracking_number}
                                  </Badge>
                                  {row.tracking_url && (
                                    <a
                                      href={row.tracking_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1"
                                      data-testid={`order-tracking-link-${row.product_id}`}
                                    >
                                      Track <ExternalLink className="h-3 w-3" />
                                    </a>
                                  )}
                                  <button
                                    type="button"
                                    className="text-xs text-stone-400 hover:text-rose-600 flex items-center gap-0.5"
                                    disabled={!!updating[`${key}:track`]}
                                    onClick={() => saveTracking(row, true)}
                                    data-testid={`order-tracking-clear-${row.product_id}`}
                                  >
                                    <X className="h-3 w-3" /> remove
                                  </button>
                                </div>
                                {row.tracking_sms_sent_at && (
                                  <span
                                    className="text-[11px] text-stone-400 ml-1"
                                    data-testid={`order-tracking-sms-sent-${row.product_id}`}
                                  >
                                    SMS sent to client {formatRelativeTime(row.tracking_sms_sent_at)}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-1 max-w-md" data-testid={`order-tracking-form-${row.product_id}`}>
                                <Input
                                  type="text"
                                  placeholder="Paste tracking number"
                                  value={trackingDrafts[`${row.booking_id}:${row.product_id}`] || ''}
                                  onChange={(e) => setTrackingDrafts((d) => ({
                                    ...d,
                                    [`${row.booking_id}:${row.product_id}`]: e.target.value,
                                  }))}
                                  className="h-8 text-sm font-mono"
                                  data-testid={`order-tracking-input-${row.product_id}`}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={!!updating[`${key}:track`]}
                                  onClick={() => saveTracking(row)}
                                  className="h-8 px-3 text-xs"
                                  data-testid={`order-tracking-save-${row.product_id}`}
                                >
                                  Save
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {(allowedNext.length > 0 || (row.payment_status === 'paid' && row.client_email)) && (
                      <div className="flex flex-wrap gap-2 lg:flex-col lg:items-stretch lg:w-44 shrink-0">
                        {row.payment_status === 'paid' && row.client_email && (
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!!updating[`${key}:slip`]}
                              onClick={() => sendPackingSlip(row)}
                              className="border-[#D4AF37] text-[#1A1A1A] hover:bg-[#D4AF37]/10"
                              data-testid={`order-email-slip-${row.product_id}`}
                            >
                              <Send className="h-3.5 w-3.5 mr-1.5" />
                              {row.packing_slip_send_count > 0 ? 'Resend slip' : 'Send packing slip'}
                            </Button>
                            {row.packing_slip_last_sent_at && (
                              <span
                                className="text-[11px] text-stone-400 text-center lg:text-left"
                                data-testid={`order-slip-last-sent-${row.product_id}`}
                              >
                                Sent {formatRelativeTime(row.packing_slip_last_sent_at)}
                              </span>
                            )}
                          </div>
                        )}
                        {allowedNext.map((next) => (
                          <Button
                            key={next}
                            size="sm"
                            variant={next === 'cancelled' ? 'outline' : 'default'}
                            disabled={!!updating[key]}
                            onClick={() => updateStatus(row, next)}
                            className={next !== 'cancelled' ? 'bg-[#1A1A1A] hover:bg-stone-800 text-white' : ''}
                            data-testid={`order-advance-${row.product_id}-${next}`}
                          >
                            Mark {STATE_LABELS[next]}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
